import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import '../App.css';

import Loader from '../components/Loader';
import Spinner from '../components/Spinner';
import BrandLogo from '../components/BrandLogo';
import {
  ArrowLeftIcon,
  AlertIcon,
  CheckIcon,
  ShieldIcon,
  TagIcon,
  ChatIcon,
  MailIcon,
} from '../components/Icons';
import { BASE_URL } from '../App';
import {
  trackProductViewed,
  trackCheckoutStarted,
  trackCouponApplied,
  trackCouponRejected,
  trackEmailEntered,
  identifyBuyer,
  trackErrorShown,
  useTrackError,
} from '../utils/analytics';
import {
  readSelectedItem,
  writeSelectedItem,
  readPortalConfig,
  writePortalConfig,
} from '../utils/portalConfig';
import {
  formatMoney,
  getDiscountPercent,
  getItemId,
  getPlanSubtitle,
  apiErrorMessage,
} from '../utils/format';

const cx = (...parts) => parts.filter(Boolean).join(' ');

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

/** The cached item is only usable if it is the plan this URL asks for.
 *  Otherwise opening /plan-details/<new> after viewing another plan showed —
 *  and charged for — the previously viewed one. */
const cachedPlanFor = (planId) => {
  const cached = readSelectedItem();
  if (!cached || cached.is_bundle) return null;
  return String(getItemId(cached)) === String(planId) ? cached : null;
};

const PlanDetails = () => {
  const { planId } = useParams();
  const navigate = useNavigate();

  const [selectedPlan, setSelectedPlan] = useState(() => cachedPlanFor(planId));
  const [portalConfig, setPortalConfig] = useState(readPortalConfig);

  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [purchaseError, setPurchaseError] = useState('');

  const [showCouponInput, setShowCouponInput] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [isApplying, setIsApplying] = useState(false);
  const [couponError, setCouponError] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null); // { code, finalAmount }

  const [isLoadingPlan, setIsLoadingPlan] = useState(false);
  const [planLoadError, setPlanLoadError] = useState(null);
  useTrackError('plan_load', planLoadError);

  const emailFieldRef = useRef(null);

  // Hyperface members are already identified by the partner, so we don't ask
  // for an email.
  const isHyperface = portalConfig?.display_name?.toLowerCase() === 'hyperface';

  /* ---------------- Load ---------------- */

  useEffect(() => {
    // A different plan in the URL invalidates whatever is in state.
    setSelectedPlan(cachedPlanFor(planId));
    setAppliedCoupon(null);
    setCouponCode('');
    setPurchaseError('');
  }, [planId]);

  useEffect(() => {
    let cancelled = false;

    const loadPlan = async () => {
      if (selectedPlan || !planId) return;

      const portalToken = localStorage.getItem('portalToken');

      if (!portalToken) {
        // Come back here once the partner token is issued.
        sessionStorage.setItem('returnTo', `/plan-details/${planId}`);
        const partnerSlug = localStorage.getItem('partnerSlug');
        navigate(partnerSlug ? `/${partnerSlug}` : '/');
        return;
      }

      setIsLoadingPlan(true);
      setPlanLoadError(null);

      try {
        const requestOptions = {
          method: 'GET',
          headers: { Authorization: `Bearer ${portalToken}` },
          redirect: 'follow',
        };

        const response = await fetch(
          `${BASE_URL}/partner/v2/get-portal-plans`,
          requestOptions
        );

        if (response.status === 401) {
          localStorage.removeItem('portalToken');
          sessionStorage.setItem('returnTo', `/plan-details/${planId}`);
          const partnerSlug = localStorage.getItem('partnerSlug');
          navigate(partnerSlug ? `/${partnerSlug}` : '/');
          return;
        }

        const result = await response.json();

        if (cancelled) return;

        if (!Array.isArray(result.plans)) {
          setPlanLoadError('Unable to load plan details.');
          return;
        }

        const plan = result.plans.find(
          (p) => !p.is_bundle && String(getItemId(p)) === String(planId)
        );

        if (!plan) {
          setPlanLoadError(
            'This plan is no longer available in your marketplace.'
          );
          return;
        }

        setSelectedPlan(plan);
        writeSelectedItem(plan);

        if (!portalConfig) {
          const configResponse = await fetch(
            `${BASE_URL}/partner/v2/get-portal-configuration`,
            requestOptions
          );
          if (configResponse.ok) {
            const configResult = await configResponse.json();
            if (!cancelled) {
              setPortalConfig(configResult);
              writePortalConfig(configResult);
            }
          }
        }
      } catch (err) {
        console.error('Error loading plan:', err);
        if (!cancelled) {
          setPlanLoadError('Failed to load plan details. Please try again.');
        }
      } finally {
        if (!cancelled) setIsLoadingPlan(false);
      }
    };

    loadPlan();
    return () => {
      cancelled = true;
    };
    // portalConfig is read, not tracked — refetching on config arrival would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planId, selectedPlan, navigate]);

  // Keyed on the id: the cached plan is re-read (a new object) on mount,
  // which used to report the same view twice.
  const viewedPlanId = selectedPlan ? String(getItemId(selectedPlan)) : null;
  useEffect(() => {
    if (viewedPlanId) trackProductViewed(selectedPlan);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewedPlanId]);

  /* ---------------- Derived pricing ---------------- */

  const discount = useMemo(
    () => getDiscountPercent(selectedPlan?.plan_mrp, selectedPlan?.plan_cost),
    [selectedPlan]
  );

  const payable = appliedCoupon?.finalAmount ?? selectedPlan?.plan_cost;
  const currency = selectedPlan?.currency;

  /* ---------------- Coupon ---------------- */

  const handleApplyCoupon = async () => {
    const code = couponCode.trim();
    if (!code) return;

    setIsApplying(true);
    setCouponError('');
    setAppliedCoupon(null);

    try {
      const response = await fetch(
        `${BASE_URL}/partner/v2/validate-portal-discount`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('portalToken')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ code, plan_id: selectedPlan.plan_id }),
          redirect: 'follow',
        }
      );

      const result = await response.json().catch(() => ({}));

      if (response.ok && Number.isFinite(Number(result.final_amount))) {
        setAppliedCoupon({ code, finalAmount: Number(result.final_amount) });
        trackCouponApplied({ item: selectedPlan, finalAmount: result.final_amount });
      } else {
        setCouponError(apiErrorMessage(result, 'That coupon code is not valid.'));
        // The API message can echo the code back, so only the status is sent.
        trackCouponRejected({ item: selectedPlan, reason: `http_${response.status}` });
      }
    } catch (err) {
      console.error('Coupon validation failed:', err);
      setCouponError('Could not check that code. Please try again.');
      trackCouponRejected({ item: selectedPlan, reason: 'network_error' });
    } finally {
      setIsApplying(false);
    }
  };

  // Editing the code after a successful apply must drop the discount, or we'd
  // show one price and send a different (unvalidated) code at checkout.
  const handleCouponChange = (value) => {
    setCouponCode(value);
    if (appliedCoupon) setAppliedCoupon(null);
    if (couponError) setCouponError('');
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    setCouponError('');
  };

  /* ---------------- Purchase ---------------- */

  const handlePurchase = useCallback(async () => {
    setPurchaseError('');

    if (!isHyperface) {
      const trimmed = email.trim();
      if (!trimmed) {
        setEmailError('Please enter your email to continue');
        emailFieldRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        emailFieldRef.current?.focus({ preventScroll: true });
        return;
      }
      if (!isValidEmail(trimmed)) {
        setEmailError('Please enter a valid email address');
        emailFieldRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        emailFieldRef.current?.focus({ preventScroll: true });
        return;
      }
    }

    setEmailError('');
    setIsProcessing(true);
    trackCheckoutStarted({
      item: selectedPlan,
      amount: appliedCoupon?.finalAmount ?? selectedPlan?.plan_cost,
      hasCoupon: Boolean(appliedCoupon),
    });

    try {
      const response = await fetch(`${BASE_URL}/partner/v2/create-portal-order`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('portalToken')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          plan_id: selectedPlan.plan_id,
          user_details: { email: isHyperface ? '' : email.trim() },
          unique_request_id: `${Date.now()}-${Math.random()
            .toString(36)
            .substring(2, 15)}`,
          ...(appliedCoupon ? { discount_code: appliedCoupon.code } : {}),
        }),
        redirect: 'follow',
      });

      const result = await response.json().catch(() => ({}));

      // Previously any reply — including a 400 — was pushed into /processing,
      // which then sat on a spinner forever because there was no order_id.
      if (!response.ok || !result?.order_id) {
        setIsProcessing(false);
        // Status only: the API message could echo what the member typed.
        trackErrorShown({ context: 'create_order', reason: `http_${response.status}` });
        setPurchaseError(
          apiErrorMessage(
            result,
            'We could not start this order. Please try again, or contact support if it keeps happening.'
          )
        );
        return;
      }

      // Pseudonymous id from the backend, never the email itself.
      identifyBuyer(result.buyer_id);
      navigate('/processing', { state: { orderResponse: result } });
    } catch (err) {
      console.error('Order creation failed:', err);
      setIsProcessing(false);
      trackErrorShown({ context: 'create_order', reason: 'network_error' });
      setPurchaseError(
        'Something went wrong reaching our servers. Please check your connection and try again.'
      );
    }
  }, [appliedCoupon, email, isHyperface, navigate, selectedPlan]);

  /* ---------------- States ---------------- */

  if (isLoadingPlan) {
    return (
      <div className="page">
        <div className="statusWrap">
          <Loader />
          <p className="statusBody">Loading plan details…</p>
        </div>
      </div>
    );
  }

  if (planLoadError) {
    return (
      <div className="page">
        <div className="statusWrap">
          <div className="statusCard">
            <div className="statusIcon statusIconBad" aria-hidden="true">
              <AlertIcon width="28" height="28" />
            </div>
            <h1 className="statusTitle">Plan unavailable</h1>
            <p className="statusBody">{planLoadError}</p>
            <div className="statusActions">
              <button
                type="button"
                className={cx('btn', 'btnMd', 'btnBrand')}
                onClick={() => navigate('/marketplace')}
              >
                Browse all plans
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!selectedPlan) {
    return (
      <div className="page">
        <div className="statusWrap">
          <div className="statusCard">
            <h1 className="statusTitle">No plan selected</h1>
            <p className="statusBody">Pick a plan from the marketplace to continue.</p>
            <div className="statusActions">
              <button
                type="button"
                className={cx('btn', 'btnMd', 'btnBrand')}
                onClick={() => navigate('/marketplace')}
              >
                Back to marketplace
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* The CTA stays clickable with an empty email on purpose: tapping it scrolls
     to the field and names what's missing. A dead grey button on the mobile
     buy bar just looked broken. */
  const purchaseBlocked = isProcessing;

  const checkoutPanel = (
    <>
      <div className="card">
        <div className="cardTitle">Price details</div>

        {discount ? (
          <div className="priceRow">
            <span>MRP</span>
            <span className="priceValue priceStrike">
              {formatMoney(selectedPlan.plan_mrp, currency)}
            </span>
          </div>
        ) : null}

        <div className="priceRow">
          <span>Plan price</span>
          <span className="priceValue">
            {formatMoney(selectedPlan.plan_cost, currency)}
          </span>
        </div>

        {discount ? (
          <div className="priceRow">
            <span>Partner discount</span>
            <span className="priceSave">{discount}% off</span>
          </div>
        ) : null}

        {appliedCoupon && (
          <div className="priceRow">
            <span>Coupon “{appliedCoupon.code}”</span>
            <span className="priceSave">
              −{formatMoney(selectedPlan.plan_cost - appliedCoupon.finalAmount, currency)}
            </span>
          </div>
        )}

        <div className="priceRow priceRowTotal">
          <span>You pay</span>
          <span className="priceTotal">{formatMoney(payable, currency)}</span>
        </div>
      </div>

      {/* Coupon */}
      <div className="card">
        <button
          type="button"
          className="disclosure"
          onClick={() => setShowCouponInput((v) => !v)}
          aria-expanded={showCouponInput}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <TagIcon />
            Apply a coupon
          </span>
          <span className="disclosureIcon" aria-hidden="true">
            {showCouponInput ? '−' : '+'}
          </span>
        </button>

        {showCouponInput && (
          <div style={{ marginTop: 14 }}>
            <div className="inputRow">
              <input
                className="input"
                type="text"
                value={couponCode}
                onChange={(e) => handleCouponChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleApplyCoupon();
                }}
                placeholder="Enter coupon code"
                aria-label="Coupon code"
              />
              <button
                type="button"
                className={cx('btn', 'btnLg', 'btnInk')}
                onClick={handleApplyCoupon}
                disabled={!couponCode.trim() || isApplying}
              >
                {isApplying ? <Spinner onBrand /> : 'Apply'}
              </button>
            </div>

            {couponError && (
              <p className="fieldError">
                <AlertIcon />
                {couponError}
              </p>
            )}

            {appliedCoupon && (
              <p className="fieldOk">
                <CheckIcon />
                Coupon applied — new price {formatMoney(appliedCoupon.finalAmount, currency)}
                <button
                  type="button"
                  className="btn btnSm"
                  style={{
                    background: 'none',
                    color: 'var(--text-link)',
                    height: 'auto',
                    padding: '0 0 0 6px',
                  }}
                  onClick={removeCoupon}
                >
                  Remove
                </button>
              </p>
            )}
          </div>
        )}
      </div>

      {/* Contact */}
      {!isHyperface && (
        <div className="card">
          <div className="cardTitle">Where should we send it?</div>
          <label className="label" htmlFor="purchase-email">
            Email address
          </label>
          <input
            id="purchase-email"
            ref={emailFieldRef}
            className={cx('input', emailError && 'inputError')}
            type="email"
            autoComplete="email"
            inputMode="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (emailError) setEmailError('');
            }}
            onBlur={() => {
              if (email && isValidEmail(email.trim())) trackEmailEntered();
            }}
            placeholder="you@example.com"
            aria-invalid={Boolean(emailError)}
            aria-describedby="purchase-email-hint"
          />
          {emailError ? (
            <p className="fieldError" id="purchase-email-hint">
              <AlertIcon />
              {emailError}
            </p>
          ) : (
            <p className="fieldHint" id="purchase-email-hint">
              Your voucher and receipt go here.
            </p>
          )}
        </div>
      )}

      {purchaseError && (
        <div className="card" style={{ borderColor: 'var(--danger)' }}>
          <p className="fieldError" style={{ marginTop: 0 }}>
            <AlertIcon />
            {purchaseError}
          </p>
        </div>
      )}

      <button
        type="button"
        className={cx('btn', 'btnLg', 'btnBrand', 'btnBlock')}
        style={{ marginTop: 16 }}
        onClick={handlePurchase}
        disabled={purchaseBlocked}
      >
        {isProcessing ? (
          <>
            <Spinner onBrand />
            Starting checkout…
          </>
        ) : (
          `Pay ${formatMoney(payable, currency)}`
        )}
      </button>

      <div className="dealTrust">
        <ShieldIcon />
        Secure checkout · Instant delivery
      </div>
    </>
  );

  return (
    <div className="page">
      <div className="detailShell">
        <button type="button" className="backLink" onClick={() => navigate('/marketplace')}>
          <ArrowLeftIcon />
          Back to marketplace
        </button>

        <div className="detailGrid">
          {/* ---------- Main ---------- */}
          <div>
            <div className="card">
              <div className="detailHero">
                <BrandLogo
                  className="detailLogo"
                  src={selectedPlan.vendor_logo_url}
                  name={selectedPlan.vendor_name}
                />
                <div style={{ minWidth: 0 }}>
                  <h1 className="detailBrand">{selectedPlan.vendor_name}</h1>
                  {getPlanSubtitle(selectedPlan) && (
                    <div className="detailPlanName">{getPlanSubtitle(selectedPlan)}</div>
                  )}
                </div>
                {discount ? (
                  <span className="dealBadge" style={{ marginLeft: 'auto' }}>
                    {discount}% OFF
                  </span>
                ) : null}
              </div>

              {selectedPlan.plan_description && (
                <p className="detailDesc">{selectedPlan.plan_description}</p>
              )}
            </div>

            <div className="card">
              <div className="cardTitle">Need a hand?</div>
              <p className="howBody" style={{ marginTop: 0 }}>
                Something not right with checkout? We usually reply within minutes.
              </p>
              <div className="supportRow">
                <a
                  className={cx('btn', 'btnSm', 'btnSecondary')}
                  href="https://wa.me/919148477518"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ChatIcon />
                  WhatsApp support
                </a>
                <a
                  className={cx('btn', 'btnSm', 'btnSecondary')}
                  href="mailto:help@getfleek.app"
                >
                  <MailIcon />
                  Email support
                </a>
              </div>
            </div>
          </div>

          {/* ---------- Checkout ---------- */}
          <aside className="detailAside">{checkoutPanel}</aside>
        </div>

        <div className="pageFoot" />
      </div>

      {/* Mobile sticky bar mirrors the marketplace buy bar. */}
      <div className="buyBar">
        <div>
          <div className="buyBarLabel">Total</div>
          <div className="buyBarPrice">{formatMoney(payable, currency)}</div>
        </div>
        <button
          type="button"
          className={cx('btn', 'btnMd', 'btnBrand', 'buyBarCta')}
          onClick={handlePurchase}
          disabled={purchaseBlocked}
        >
          {isProcessing ? <Spinner onBrand /> : 'Pay now'}
        </button>
      </div>
    </div>
  );
};

export default PlanDetails;
